import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Layers, PanelLeftClose } from "lucide-react";
import { ReactComponent as IconBuilding } from "../../../shared/assets/Icons/IconBuilding.svg";
import { ReactComponent as Geolocate } from "../../../shared/assets/geolocation.svg";
import { ReactComponent as IconServer } from "../../../shared/assets/Icons/IconServer.svg";
import GenericInput from "@ui-components/Input/GenericInput";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { SkeletonLine } from "@ui-components/Skeleton/GenericSkeleton";
import { DataStateView } from "../../../kontrolEdge/components/Backbone/BackboneDetails/StateViews/DataStateView";
import { useCloudInventorySource } from "../../hooks/useCloudInventorySource";
import { canonicalSourceId, filterInventoryForInsight, findEntityPath, type InventoryCluster, type InventoryReflector } from "../../utils/insightScopeTree";
import type { CloudInventory } from "../../../ctxReflectorV2/components/BGP/BgpVisibilityData";
import type { InsightScopeState } from "@redux/slices/insightScopeSlice";
import type { InsightSourceType } from "../../types/insightApi";

const LEAF_PAGE_SIZE = 50;

export interface InsightScopeBrowserProps {
	scope: InsightScopeState;
	onActivateScope: (
		sourceType: InsightSourceType,
		sourceId: string,
		sourceName: string,
		cloud: string | null,
		region: string | null,
		account: string | null
	) => void;
	/**
	 * Collapse the browser. The toggle lives here in the panel header (CTX-112
	 * follow-up) rather than the shared page-header band; when omitted (e.g. in
	 * unit tests or a host that manages its own chrome) no collapse control
	 * renders. The matching *expand* affordance is the host's job — once
	 * collapsed this panel is unmounted, so it cannot host its own re-open.
	 */
	onCollapse?: () => void;
}

function entityStatusVariant(status: string): "success" | "warning" | "error" | "default" {
	if (status === "online" || status === "Healthy") return "success";
	if (status === "degraded" || status === "Degraded") return "warning";
	if (status === "offline" || status === "Offline") return "error";
	return "default";
}

function nodeKey(...parts: string[]): string {
	return parts.join("::");
}

/** A cluster's raw member entry as returned inside `region.clusters[].reflectors` — loosely typed upstream (`any[]`), so every field here is read defensively. */
interface InventoryClusterMember {
	id?: string;
	subId?: string;
	display_name?: string;
	name?: string;
	status?: string;
}

interface EntityRowProps {
	entity: InventoryReflector | InventoryCluster;
	isActive: boolean;
	onSelect: () => void;
}

const EntityRow: React.FC<EntityRowProps> = ({ entity, isActive, onSelect }) => {
	const isCluster = entity.type === "cluster";
	const memberCount = isCluster
		? (((entity as InventoryCluster).reflectors as InventoryClusterMember[] | undefined) ?? []).length
		: null;
	const status = isCluster ? (entity as InventoryCluster).health : (entity as InventoryReflector).status;
	// The CTXR UUID under a standalone reflector, mirroring BGP Visibility's
	// sidebar — it is the identity the topology request is keyed on, so seeing
	// it here is what lets a support engineer correlate the view with a digest.
	// A cluster shows its member count instead: the members themselves are
	// switched on the canvas (per-member scoping), not inspected in the tree.
	const subtitle = isCluster
		? `${memberCount} member${memberCount === 1 ? "" : "s"}`
		: canonicalSourceId(entity);

	return (
		<li>
			<button
				type="button"
				role="treeitem"
				aria-selected={isActive}
				data-testid={`entity-leaf-${entity.type}-${entity.id}`}
				onClick={onSelect}
				title={entity.display_name}
				className={`flex w-full items-center gap-2 rounded-sm px-3 py-1.5 text-left transition-colors ${
					isActive
						? "border-l-2 border-semantic-outline-primary bg-semantic-bg-brand-primary text-semantic-text-brand"
						: "text-semantic-text-secondary hover:bg-semantic-state-active"
				}`}
			>
				{isCluster ? (
					<Layers className="h-4 w-4 shrink-0 text-semantic-text-secondary" aria-hidden="true" />
				) : (
					<IconServer className="h-4 w-4 shrink-0 text-semantic-text-secondary" aria-hidden="true" />
				)}
				<span className="flex min-w-0 flex-1 flex-col">
					<span className="truncate text-body text-semantic-text-secondary">{entity.display_name}</span>
					<span className="truncate text-body-small text-semantic-text-tertiary" title={subtitle}>
						{subtitle}
					</span>
				</span>
				{/* Preliminary-version simplification (2026-08-04): an "unknown"
				    operational status is noise, not signal — show a tag only when
				    the inventory actually reports one. */}
				{status && status.toLowerCase() !== "unknown" && (
					<GenericTag label={status} variant={entityStatusVariant(status)} size="small" />
				)}
			</button>
		</li>
	);
};

/**
 * Source-first hierarchical scope browser (CTX-97 "reuse the existing BGP
 * Visibility inventory pipeline" correction). Renders the exact same
 * tenant-scoped `CloudInventory` tree BGP Visibility already fetches via
 * `getCloudInventorySidebar` (see `useCloudInventorySource`), presented as
 * Cloud -> Account -> Region -> Reflector/Cluster — never a second discovery
 * pipeline keyed off `/api/v2/insight/topology/sources`.
 *
 * The **Reflectors | Clusters** control is a client-side filter over that
 * one cached tree (`filterInventoryForInsight`), not a second fetch: clicking
 * the tab never issues a new request. Selecting a leaf carries its full
 * Cloud/Account/Region context in one click — there is no separate
 * Cloud -> Region -> Account cascade after source selection, since the
 * sidebar's own hierarchy already scopes each Reflector/Cluster occurrence to
 * one specific Cloud/Account/Region.
 */
export const InsightScopeBrowser: React.FC<InsightScopeBrowserProps> = ({ scope, onActivateScope, onCollapse }) => {
	const { status, cloudInventory, errorMessage, refetch } = useCloudInventorySource();
	const [search, setSearch] = useState("");
	const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
	const [leafPageByRegion, setLeafPageByRegion] = useState<Record<string, number>>({});

	const needle = search.trim();
	const filtered: CloudInventory = useMemo(
		() => filterInventoryForInsight(cloudInventory, needle),
		[cloudInventory, needle]
	);

	// Default-expand every cloud so the tenant's providers are visible at a
	// glance; account/region stay collapsed until picked, bounding the DOM for
	// tenants with many accounts/regions. A non-empty search auto-expands
	// every surviving node (the filtered tree already only contains matching
	// branches) so results are never hidden behind a collapsed ancestor.
	useEffect(() => {
		if (!cloudInventory) return;
		setExpandedKeys((prev) => {
			const next = new Set(prev);
			cloudInventory.clouds.forEach((cloud) => next.add(nodeKey("cloud", cloud.id)));
			return next;
		});
	}, [cloudInventory]);

	// Deep-link restoration: once the tree loads, locate the active
	// selection's Cloud/Account/Region ancestry and expand it, so a refreshed
	// URL reveals the authorized path without manual navigation.
	useEffect(() => {
		if (!cloudInventory || !scope.sourceType || !scope.sourceId) return;
		const path = findEntityPath(cloudInventory, scope.sourceType, scope.sourceId);
		if (!path) return;
		setExpandedKeys((prev) => {
			const next = new Set(prev);
			next.add(nodeKey("cloud", path.cloudId));
			next.add(nodeKey("cloud", path.cloudId, "account", path.accountId));
			next.add(nodeKey("cloud", path.cloudId, "account", path.accountId, "region", path.regionId));
			return next;
		});
		// Only re-run when the active source identity changes, not on every render.
	}, [cloudInventory, scope.sourceType, scope.sourceId]);

	const isExpanded = useCallback((key: string) => needle.length > 0 || expandedKeys.has(key), [expandedKeys, needle]);

	const toggleExpanded = useCallback((key: string) => {
		setExpandedKeys((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	}, []);

	const handleSelect = useCallback(
		(entity: InventoryReflector | InventoryCluster, cloudName: string, accountId: string, regionName: string) => {
			onActivateScope(entity.type, canonicalSourceId(entity), entity.display_name, cloudName, regionName, accountId);
		},
		[onActivateScope]
	);

	const totalClouds = filtered.clouds.length;
	const isInitialLoading = status === "loading" && !cloudInventory;

	return (
		// Flush panel, not a card: the host `<aside>` owns the 1px right border that
		// separates it from the canvas (BGP Visibility's shape). Rounded corners here
		// left a visible seam against that border.
		<div className="flex h-full min-h-0 flex-col overflow-hidden bg-semantic-bg-primary">
			<div className="flex flex-col gap-2 px-3 py-2.5">
				<div className="flex items-center gap-2">
					{/*
						During the first load the search slot holds a placeholder bar
						rather than a live (but useless) input — BGP Visibility's sidebar
						skeleton does the same, and rendering the real search box on top
						of the skeleton made the panel look like it had two headers.
						Keeping the slot occupied means nothing shifts when data lands.
					*/}
					<div className="min-w-0 flex-1">
						{isInitialLoading ? (
							<SkeletonLine w="100%" h={36} className="rounded" />
						) : (
							<GenericInput
								value={search}
								onChange={setSearch}
								type="search"
								placeholder="Search sources, clouds, accounts, regions"
								fullWidth
							/>
						)}
					</div>
					{onCollapse && (
						<button
							type="button"
							aria-label="Collapse scope browser"
							data-testid="scope-browser-collapse"
							onClick={onCollapse}
							className="shrink-0 rounded-sm border border-semantic-border-secondary p-1.5 text-semantic-text-secondary hover:bg-semantic-state-active"
						>
							<PanelLeftClose className="h-4 w-4" aria-hidden="true" />
						</button>
					)}
				</div>
				{/* Standalone Reflectors and Clusters are listed together inside each
				    region (see the leaf list below) instead of behind a source-type
				    toggle — same shape as BGP Visibility's sidebar. A tenant whose
				    reflectors are deployed as HA clusters has no standalone entries at
				    all, so a Reflectors-only browser showed them an empty region
				    (2026-08-06). The row itself carries the distinction: Layers icon,
				    member count, and an expandable member list. */}
			</div>
			<div className="min-h-0 flex-1 overflow-y-auto border-t border-semantic-border-secondary pb-8">
				<DataStateView
					loading={isInitialLoading}
					loadingContent={
						// Same skeleton shape BGP Visibility's sidebar uses — tapered
						// cloud/account/region groups, so the placeholder reads as a tree
						// instead of full-width slabs. The header bar lives in the search
						// slot above (see the comment there), not here.
						<div
							className="flex flex-col gap-3 px-3 py-2.5"
							data-testid="scope-browser-loading"
							role="status"
							aria-busy="true"
						>
							{[1, 2, 3, 4, 5].map((group) => (
								<div key={group} className="flex flex-col gap-2">
									<SkeletonLine w="80%" h={20} className="rounded" />
									<SkeletonLine w="90%" h={16} className="rounded" />
									<SkeletonLine w="70%" h={16} className="rounded" />
								</div>
							))}
						</div>
					}
					error={status === "error" ? errorMessage || "Failed to load your authorized sources" : null}
					errorTitle="Failed to load sources"
					onRetry={refetch}
					isEmpty={totalClouds === 0}
					emptyTitle={search ? "No matching sources" : "No authorized sources"}
					emptyDescription={
						search
							? "Try a different search term, or clear the search to see all authorized sources."
							: "Your tenant has no authorized Reflectors or Clusters of this type yet."
					}
				>
					<ul className="flex flex-col" data-testid="scope-browser-tree" role="tree" aria-label="Insight sources">
						{filtered.clouds.map((cloud) => {
							const cloudKey = nodeKey("cloud", cloud.id);
							const cloudExpanded = isExpanded(cloudKey);
							return (
								<li key={cloud.id}>
									<button
										type="button"
										data-testid={`cloud-toggle-${cloud.id}`}
										aria-expanded={cloudExpanded}
										onClick={() => toggleExpanded(cloudKey)}
										className="flex w-full items-center justify-between px-3 py-2 text-left"
									>
										<span className="text-body-small font-medium uppercase tracking-wide text-semantic-text-tertiary">{cloud.name}</span>
										{cloudExpanded ? (
											<ChevronDown className="h-4 w-4 shrink-0 text-semantic-text-secondary" aria-hidden="true" />
										) : (
											<ChevronRight className="h-4 w-4 shrink-0 text-semantic-text-secondary" aria-hidden="true" />
										)}
									</button>
									{cloudExpanded && (
										<ul>
											{cloud.accounts.map((account) => {
												const accountKey = nodeKey("cloud", cloud.id, "account", account.id);
												const accountExpanded = isExpanded(accountKey);
												return (
													<li key={account.id}>
														<button
															type="button"
															data-testid={`account-toggle-${cloud.id}-${account.id}`}
															aria-expanded={accountExpanded}
															onClick={() => toggleExpanded(accountKey)}
															className="flex w-full items-center justify-between gap-2 px-4 py-2 text-left hover:bg-semantic-state-active"
														>
															<span className="flex min-w-0 items-center gap-2">
																<IconBuilding className="h-5 w-5 shrink-0 text-semantic-text-secondary" aria-hidden="true" />
																<span className="min-w-0">
																	<span title={account.name} className="block truncate text-body text-semantic-text-secondary">{account.name}</span>
																	<span title={account.id} className="block truncate text-body-small text-semantic-text-secondary">{account.id}</span>
																</span>
															</span>
															{accountExpanded ? (
																<ChevronDown className="h-3.5 w-3.5 shrink-0 text-semantic-text-secondary" aria-hidden="true" />
															) : (
																<ChevronRight className="h-3.5 w-3.5 shrink-0 text-semantic-text-secondary" aria-hidden="true" />
															)}
														</button>
														{accountExpanded && (
															<ul>
																{account.regions.map((region) => {
																	const regionKey = nodeKey("cloud", cloud.id, "account", account.id, "region", region.id);
																	const regionExpanded = isExpanded(regionKey);
																	// Reflectors first, then Clusters — one flat list per
																	// region, matching BGP Visibility's ordering.
																	const entities: (InventoryReflector | InventoryCluster)[] = [
																		...region.reflectors,
																		...region.clusters
																	];
																	const page = leafPageByRegion[regionKey] ?? 1;
																	const visibleEntities = entities.slice(0, page * LEAF_PAGE_SIZE);
																	return (
																		<li key={region.id}>
																			<button
																				type="button"
																				data-testid={`region-toggle-${cloud.id}-${account.id}-${region.id}`}
																	aria-expanded={regionExpanded}
																	onClick={() => toggleExpanded(regionKey)}
																	className="flex w-full items-center justify-between px-6 py-1.5 text-left hover:bg-semantic-state-active"
																>
																	<span className="flex items-center gap-2">
																		<Geolocate className="h-4 w-4 shrink-0 text-semantic-text-secondary" aria-hidden="true" />
																		<span className="text-body text-semantic-text-secondary">{region.name}</span>
																	</span>
																	{regionExpanded ? (
																		<ChevronDown className="h-3 w-3 shrink-0 text-semantic-text-secondary" aria-hidden="true" />
																	) : (
																		<ChevronRight className="h-3 w-3 shrink-0 text-semantic-text-secondary" aria-hidden="true" />
																	)}
																			</button>
																			{regionExpanded && (
																				<ul className="flex flex-col gap-0.5 px-6 py-1" role="group">
																					{entities.length === 0 && (
																						<li
																							className="px-3 py-1 text-xs text-semantic-text-tertiary"
																							data-testid={`no-entities-${cloud.id}-${account.id}-${region.id}`}
																						>
																							No reflectors or clusters in this region.
																						</li>
																					)}
																					{visibleEntities.map((entity) => (
																						<EntityRow
																							key={`${entity.type}-${entity.id}`}
																							entity={entity}
																							isActive={
																								scope.sourceType === entity.type &&
																								scope.sourceId === canonicalSourceId(entity)
																							}
																							onSelect={() => handleSelect(entity, cloud.name, account.id, region.name)}
																						/>
																					))}
																					{entities.length > visibleEntities.length && (
																						<li>
																							<button
																								type="button"
																								data-testid={`show-more-entities-${regionKey}`}
																								onClick={() =>
																									setLeafPageByRegion((prev) => ({ ...prev, [regionKey]: page + 1 }))
																								}
																								className="px-3 py-1 text-xs font-medium text-semantic-text-brand hover:underline"
																							>
																								Show {Math.min(LEAF_PAGE_SIZE, entities.length - visibleEntities.length)} more
																							</button>
																						</li>
																					)}
																				</ul>
																			)}
																		</li>
																	);
																})}
															</ul>
														)}
													</li>
												);
											})}
										</ul>
									)}
								</li>
							);
						})}
					</ul>
				</DataStateView>
			</div>
		</div>
	);
};

export default InsightScopeBrowser;
